import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  Field,
  Combobox,
  Dropdown,
  Option,
  Spinner,
  Input,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useGetPlannersByBuildingMutation } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { useEventFilterPanelStyles } from './EventFilterPanel.styles';
import { filterAndSortBySelected } from '@/layouts/RoomFilterPanel/RoomFilterPanel';
import type { EventStatus } from '@/types/calendarTypes';

export interface EventFilters {
  statuses: EventStatus[];
  ownerEmails: string[];
  buildingIds: string[];
}

export const EMPTY_EVENT_FILTERS: EventFilters = {
  statuses: [],
  ownerEmails: [],
  buildingIds: [],
};

export interface BuildingOption {
  buildingId: string;
  buildingName: string;
}

interface OwnerOption {
  email: string;
  displayName: string;
}

export interface EventFilterPanelProps {
  open: boolean;
  appliedFilters: EventFilters;
  buildingOptions: BuildingOption[];
  onApply: (filters: EventFilters) => void;
  onCancel: () => void;
}

const STATUS_OPTIONS: EventStatus[] = [
  'submitted',
  'under_review',
  'confirmed',
  'declined',
  'request_for_cancellation',
  'cancelled',
  'completed',
  'did_not_occur',
];

const dedupePlannersByEmail = (planners: OwnerOption[]): OwnerOption[] => {
  const seen = new Set<string>();
  return planners.filter((p) => {
    const key = p.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const EventFilterPanel = ({
  open,
  appliedFilters,
  buildingOptions,
  onApply,
  onCancel,
}: EventFilterPanelProps) => {
  const styles = useEventFilterPanelStyles();
  const { t } = useTranslation('common');
  const [localFilters, setLocalFilters] = useState<EventFilters>(EMPTY_EVENT_FILTERS);
  const [ownerOptions, setOwnerOptions] = useState<OwnerOption[]>([]);
  const [getPlannersByBuilding] = useGetPlannersByBuildingMutation();
  const [buildingSearch, setBuildingSearch] = useState<string | undefined>(undefined);
  const [ownerSearch, setOwnerSearch] = useState<string | undefined>(undefined);
  const [buildingSnapshot, setBuildingSnapshot] = useState<Set<string>>(new Set());
  const [ownerSnapshot, setOwnerSnapshot] = useState<Set<string>>(new Set());
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setLocalFilters({ ...appliedFilters });
      setBuildingSearch(undefined);
      setOwnerSearch(undefined);
      setBuildingSnapshot(new Set(appliedFilters.buildingIds));
      setOwnerSnapshot(new Set(appliedFilters.ownerEmails));
      if (appliedFilters.buildingIds.length > 0) {
        setOwnerOptions([]);
        const BATCH_SIZE = 40;
        const batches: string[][] = [];
        for (let i = 0; i < appliedFilters.buildingIds.length; i += BATCH_SIZE) {
          batches.push(appliedFilters.buildingIds.slice(i, i + BATCH_SIZE));
        }
        const batchPromises = batches.map((batch) =>
          getPlannersByBuilding({ buildingIds: batch })
            .unwrap()
            .then((result) => {
              if (result.success && result.data) {
                const planners = result.data.flatMap((bp) => bp.planners);
                setOwnerOptions((prev) => dedupePlannersByEmail([...prev, ...planners]));
              }
            })
            .catch(() => {
              // Skip failed batch
            })
        );
        void Promise.allSettled(batchPromises);
      } else {
        setOwnerOptions([]);
      }
    }
  }, [open, appliedFilters, getPlannersByBuilding]);

  const handleStatusChange = useCallback((_e: unknown, data: { selectedOptions: string[] }) => {
    setLocalFilters((prev) => ({
      ...prev,
      statuses: data.selectedOptions as EventStatus[],
    }));
  }, []);

  const fetchPlannersByBuilding = useCallback(
    (buildingIds: string[]) => {
      if (buildingIds.length > 0) {
        setIsLoadingOwners(true);
        setOwnerOptions([]);
        // Batch into chunks of 40 and fetch in parallel; append results as each resolves
        const BATCH_SIZE = 40;
        const batches: string[][] = [];
        for (let i = 0; i < buildingIds.length; i += BATCH_SIZE) {
          batches.push(buildingIds.slice(i, i + BATCH_SIZE));
        }

        const batchPromises = batches.map((batch) =>
          getPlannersByBuilding({ buildingIds: batch })
            .unwrap()
            .then((result) => {
              if (result.success && result.data) {
                const planners = result.data.flatMap((bp) => bp.planners);
                setOwnerOptions((prev) => dedupePlannersByEmail([...prev, ...planners]));
              }
            })
            .catch(() => {
              // Skip failed batch; owners from other batches still show
            })
        );

        void Promise.allSettled(batchPromises).then(() => {
          setIsLoadingOwners(false);
        });
      } else {
        setOwnerOptions([]);
      }
    },
    [getPlannersByBuilding]
  );

  const handleBuildingChange = useCallback(
    (_e: unknown, data: { selectedOptions: string[] }) => {
      let selectedBuildingIds = data.selectedOptions.filter((id) => id !== '__select_all__');
      const allBuildingIds = buildingOptions.map((b) => b.buildingId);
      const wasAllSelected =
        allBuildingIds.length > 0 &&
        allBuildingIds.every((id) => localFilters.buildingIds.includes(id));

      if (data.selectedOptions.includes('__select_all__')) {
        selectedBuildingIds = wasAllSelected ? [] : allBuildingIds;
      }

      setLocalFilters((prev) => ({
        ...prev,
        buildingIds: selectedBuildingIds,
        ownerEmails: [],
      }));
      // Debounce the planners fetch to avoid an API call per click
      clearTimeout(debounceTimerRef.current);
      if (selectedBuildingIds.length > 0) {
        setIsLoadingOwners(true);
        debounceTimerRef.current = setTimeout(() => {
          fetchPlannersByBuilding(selectedBuildingIds);
        }, 500);
      } else {
        setIsLoadingOwners(false);
        setOwnerOptions([]);
      }
    },
    [buildingOptions, localFilters.buildingIds, fetchPlannersByBuilding]
  );

  const handleOwnerChange = useCallback((_e: unknown, data: { selectedOptions: string[] }) => {
    setLocalFilters((prev) => ({
      ...prev,
      ownerEmails: data.selectedOptions,
    }));
  }, []);

  const handleClearStatuses = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, statuses: [] }));
  }, []);

  const handleClearBuilding = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, buildingIds: [], ownerEmails: [] }));
    setOwnerOptions([]);
  }, []);

  const handleClearOwner = useCallback(() => {
    setLocalFilters((prev) => ({ ...prev, ownerEmails: [] }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localFilters);
  }, [localFilters, onApply]);

  const allBuildingsSelected =
    buildingOptions.length > 0 && localFilters.buildingIds.length === buildingOptions.length;

  const buildingSelectedOptions = useMemo(
    () =>
      allBuildingsSelected
        ? ['__select_all__', ...localFilters.buildingIds]
        : localFilters.buildingIds,
    [allBuildingsSelected, localFilters.buildingIds]
  );

  const filteredBuildings = useMemo(
    () =>
      filterAndSortBySelected(
        buildingOptions,
        buildingSearch,
        (b) => buildingSnapshot.has(b.buildingId),
        (b, q) => b.buildingName.toLowerCase().includes(q)
      ),
    [buildingOptions, buildingSearch, buildingSnapshot]
  );

  const buildingSelectedText = useMemo(
    () =>
      localFilters.buildingIds
        .map((id) => buildingOptions.find((b) => b.buildingId === id)?.buildingName ?? id)
        .join(', '),
    [localFilters.buildingIds, buildingOptions]
  );

  const statusDisplayValue =
    localFilters.statuses.length > 0
      ? localFilters.statuses.map((s) => t(`calendar.filter.options.${s}`)).join(', ')
      : '';

  const filteredOwners = useMemo(
    () =>
      filterAndSortBySelected(
        ownerOptions,
        ownerSearch,
        (o) => ownerSnapshot.has(o.email),
        (o, q) => o.displayName.toLowerCase().includes(q)
      ),
    [ownerOptions, ownerSearch, ownerSnapshot]
  );

  const ownerSelectedText = useMemo(
    () =>
      localFilters.ownerEmails
        .map((email) => ownerOptions.find((o) => o.email === email)?.displayName ?? email)
        .join(', '),
    [localFilters.ownerEmails, ownerOptions]
  );

  return (
    <OverlayDrawer
      position="end"
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) onCancel();
      }}
      data-testid="event-filter-panel"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <BasicButton
              variant="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onCancel}
              data-testid="event-filter-panel-close"
            />
          }
        >
          {t('calendar.eventsFilter.title')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        {/* Status — multi-select */}
        <div className={styles.fieldGroup}>
          <Field label={t('calendar.eventsFilter.statusLabel')}>
            <Dropdown
              multiselect
              placeholder={t('calendar.eventsFilter.notSelected')}
              selectedOptions={localFilters.statuses}
              onOptionSelect={handleStatusChange}
              value={statusDisplayValue}
              data-testid="filter-event-status"
            >
              {STATUS_OPTIONS.map((s) => (
                <Option key={s} value={s}>
                  {t(`calendar.filter.options.${s}`)}
                </Option>
              ))}
            </Dropdown>
            {localFilters.statuses.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClearStatuses}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClearStatuses();
                }}
                data-testid="clear-status-selection"
              >
                {t('calendar.eventsFilter.clearSelection')}
              </span>
            )}
          </Field>
        </div>

        {/* Building — multi-select with search */}
        <div className={styles.fieldGroup}>
          <Field label={t('calendar.eventsFilter.buildingLabel')}>
            <Combobox
              multiselect
              autoComplete="off"
              placeholder={
                localFilters.buildingIds.length > 0
                  ? t('calendar.eventsFilter.searchBuildings')
                  : t('calendar.eventsFilter.notSelected')
              }
              selectedOptions={buildingSelectedOptions}
              onOptionSelect={handleBuildingChange}
              value={buildingSearch ?? buildingSelectedText}
              onChange={(e) => setBuildingSearch((e.target as HTMLInputElement).value)}
              onOpenChange={(_e, data) => {
                if (data.open) {
                  setBuildingSearch('');
                  setBuildingSnapshot(new Set(localFilters.buildingIds));
                } else {
                  setBuildingSearch(undefined);
                }
              }}
              data-testid="filter-event-building"
              listbox={{ className: styles.comboboxListbox }}
            >
              <Option
                key="__select_all__"
                value="__select_all__"
                text={t('calendar.eventsFilter.selectAll')}
                data-testid="select-all-building"
              >
                {t('calendar.eventsFilter.selectAll')}
              </Option>
              {filteredBuildings.map((b) => (
                <Option key={b.buildingId} value={b.buildingId} text={b.buildingName}>
                  {b.buildingName}
                </Option>
              ))}
              {filteredBuildings.length === 0 && (
                <Option key="__no_match__" value="__no_match__" disabled text="">
                  No results
                </Option>
              )}
            </Combobox>
            {localFilters.buildingIds.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClearBuilding}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClearBuilding();
                }}
                data-testid="clear-building-selection"
              >
                {t('calendar.eventsFilter.clearSelection')}
              </span>
            )}
          </Field>
        </div>

        {/* Owner — multi-select with search, disabled until Building is chosen */}
        <div className={styles.fieldGroup}>
          <Field label={t('calendar.eventsFilter.ownerLabel')}>
            {isLoadingOwners ? (
              <Input
                disabled
                placeholder={t('loading')}
                contentBefore={<Spinner size="tiny" />}
                data-testid="owner-loading"
              />
            ) : (
              <Combobox
                multiselect
                autoComplete="off"
                placeholder={
                  localFilters.ownerEmails.length > 0
                    ? t('calendar.eventsFilter.searchOwners')
                    : t('calendar.eventsFilter.notSelected')
                }
                selectedOptions={localFilters.ownerEmails}
                onOptionSelect={handleOwnerChange}
                value={ownerSearch ?? ownerSelectedText}
                onChange={(e) => setOwnerSearch((e.target as HTMLInputElement).value)}
                onOpenChange={(_e, data) => {
                  if (data.open) {
                    setOwnerSearch('');
                    setOwnerSnapshot(new Set(localFilters.ownerEmails));
                  } else {
                    setOwnerSearch(undefined);
                  }
                }}
                disabled={localFilters.buildingIds.length === 0}
                data-testid="filter-event-owner"
                listbox={{ className: styles.comboboxListbox }}
              >
                {filteredOwners.map((o) => (
                  <Option key={o.email} value={o.email} text={o.displayName}>
                    {o.displayName}
                  </Option>
                ))}
                {filteredOwners.length === 0 && (
                  <Option key="__no_match__" value="__no_match__" disabled text="">
                    No results
                  </Option>
                )}
              </Combobox>
            )}
            {localFilters.ownerEmails.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClearOwner}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleClearOwner();
                }}
                data-testid="clear-owner-selection"
              >
                {t('calendar.eventsFilter.clearSelection')}
              </span>
            )}
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter className={styles.footer}>
        <BasicButton variant="primary" onClick={handleApply} data-testid="event-filter-apply">
          {t('calendar.eventsFilter.apply')}
        </BasicButton>
        <BasicButton variant="secondary" onClick={onCancel} data-testid="event-filter-cancel">
          {t('calendar.eventsFilter.cancel')}
        </BasicButton>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

EventFilterPanel.displayName = 'EventFilterPanel';
